import { ArrowDown, ArrowUp, Camera, EyeOff, Plus, Trash2 } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { dateSortKey } from '../../model/dates';
import { EVENT_LABELS, defaultPronouns, displayName, emptyName, isHiddenName, newEvent, newPerson, presumedDeceased, termGenderWithReason } from '../../model/people';
import { addNewPerson, removePerson, upsertPerson } from '../../model/tree';
import type { PersonField } from '../../model/suggestions';
import type { EventType, LifeEvent, Person, PersonName } from '../../model/types';
import { NAME_TYPE_LABELS, TERM_OPTIONS, TERM_REASONS } from '../labels';
import { closeDialog, commit, confirmAction, focusPerson, getState, hasPanel, openDialog, showPerson, toast, undo, useApp } from '../store';
import { photoFromBlob } from '../photo';
import { Avatar, Field, Modal, Select } from './common';
import { DateInput } from './DateInput';
import { GenderPicker } from './GenderPicker';
import { PlaceInput } from './PlaceInput';
import { PronounPicker } from './PronounPicker';

const EVENT_ORDER: EventType[] = [
  'birth', 'death', 'residence', 'occupation', 'education', 'graduation', 'military', 'immigration', 'emigration',
  'naturalization', 'census', 'retirement', 'adoption', 'name-change', 'gender-transition', 'hrt-start', 'coming-out', 'burial', 'cremation', 'custom',
];


/** The event a field belongs to, created (and dropped again on save if left empty) when there isn't one. */
const FIELD_EVENTS: Partial<Record<PersonField, EventType>> = { 'birth-date': 'birth', 'birth-place': 'birth', 'death-date': 'death', occupation: 'occupation' };

/**
 * Add or edit a person: the same full form either way. Without a personId it creates someone new.
 * `field` opens it at one field (a suggestion asking for a birthplace lands on the birthplace).
 */
export function PersonEditor({ personId, field }: { personId?: string; field?: PersonField }) {
  const tree = useApp((s) => s.tree)!;
  const original = personId ? tree.people[personId] : undefined;
  const isNew = !personId;
  // An event made just to hold the asked-for field, so saving without filling it in leaves no empty event.
  const added = useRef<string | null>(null);
  const [p, setP] = useState<Person>(() => {
    if (!original) return newPerson({ events: [newEvent('birth')] });
    const copy = structuredClone(original);
    const type = field && FIELD_EVENTS[field];
    // A death date goes on a death event, never the burial standing in for one (see withEventField).
    if (type && !copy.events.some((e) => e.type === type)) {
      const e = newEvent(type);
      added.current = e.id;
      copy.events.push(e);
    }
    return copy;
  });
  const formRef = useRef<HTMLFormElement>(null);
  // After the dialog has placed its own focus, move to the asked-for field and show where it is.
  useEffect(() => {
    if (!field || !original) return;
    const t = setTimeout(() => {
      const type = FIELD_EVENTS[field];
      const event = type && (type === 'occupation' ? [...p.events].reverse().find((e) => e.type === type && !e.label) ?? p.events.find((e) => e.type === type) : p.events.find((e) => e.type === type));
      const key = event ? `${field === 'occupation' ? 'label' : field === 'birth-place' ? 'place' : 'date'}:${event.id}` : field;
      const box = formRef.current?.querySelector<HTMLElement>(`[data-field="${CSS.escape(key)}"]`);
      if (!box) return;
      // Light up the whole field (label and all), or the event card or photo row it's part of.
      const shown = box.closest<HTMLElement>('.field, .photo-row') ?? box;
      shown.scrollIntoView({ block: 'center' });
      const input = box.matches('input, select, textarea, button') ? box : box.querySelector<HTMLElement>('input, select, textarea, button');
      input?.focus({ preventScroll: true });
      shown.classList.add('field-flash');
      setTimeout(() => shown.classList.remove('field-flash'), 1800);
    }, 60);
    return () => clearTimeout(t);
    // Once, when the editor opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tagText, setTagText] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  // Pronouns filled in from the gender during this edit, so they can follow a later gender change.
  const suggested = useRef('');
  const genderId = useId();
  const pronounsId = useId();
  if (personId && !original) return null;

  const set = (patch: Partial<Person>) => setP((x) => ({ ...x, ...patch }));
  const setName = (i: number, patch: Partial<PersonName>) => set({ names: p.names.map((n, j) => (j === i ? { ...n, ...patch } : n)) });
  const setEvent = (id: string, patch: Partial<LifeEvent>) => set({ events: p.events.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
  const moveName = (i: number, d: -1 | 1) => {
    const names = [...p.names];
    const j = i + d;
    if (j < 0 || j >= names.length) return;
    [names[i], names[j]] = [names[j], names[i]];
    set({ names });
  };

  const save = () => {
    let person = p;
    const extra = person.events.find((e) => e.id === added.current);
    if (extra && !extra.date && !extra.place && !extra.label && !extra.note) person = { ...person, events: person.events.filter((e) => e !== extra) };
    const pending = tagText.trim();
    if (pending && !person.tags.includes(pending)) person = { ...person, tags: [...person.tags, pending] };
    if (!person.names.length) person = { ...person, names: [emptyName()] };
    if (isNew) {
      if (!person.names.some((n) => n.given.trim() || n.surname.trim())) return toast('Enter a first or last name. Everything else is optional.', 'error');
      // The blank birth card we start with isn't an event until something is filled in.
      person = { ...person, events: person.events.filter((e) => e.date || e.place || e.note || e.label || e.type !== 'birth') };
      const t = getState().tree!;
      const { tree: next } = addNewPerson(t, person);
      commit(Object.keys(t.people).length ? next : { ...next, homePersonId: person.id });
      closeDialog();
      focusPerson(person.id);
      // Only the charts have the person panel; elsewhere, their details open in a dialog.
      if (hasPanel(getState().view)) toast(`Added ${displayName(person)}. Use the panel on the right to add family and friends.`, 'success');
      else toast(`Added ${displayName(person)}. Open their details to add family and friends.`, 'success', { label: 'Details', run: () => showPerson(person.id) });
      return;
    }
    commit(upsertPerson(getState().tree!, person));
    closeDialog();
  };

  const del = () =>
    confirmAction({
      title: `Delete ${displayName(original)}?`,
      body: 'This removes the person and all of their relationships and group memberships. You can undo this right after.',
      confirmLabel: 'Delete person',
      danger: true,
      onConfirm: () => {
        commit(removePerson(getState().tree!, personId!));
        closeDialog();
        toast(`Deleted ${displayName(original)}.`, 'info', { label: 'Undo', run: undo });
      },
    });

  const allHidden = p.names.length > 0 && p.names.every(isHiddenName);
  const auto = termGenderWithReason({ ...p, terms: 'auto' });

  return (
    <Modal
      title={isNew ? 'Add a person' : `Edit ${displayName(original)}`}
      wide
      footer={
        <>
          {!isNew && (
            <>
              <button className="btn danger left" onClick={del}>
                <Trash2 size={15} /> Delete
              </button>
              <button className="btn" onClick={() => openDialog({ type: 'merge', personId: personId! })}>
                Merge with…
              </button>
            </>
          )}
          {isNew && <span className="left faint small-text">Only a first or last name is required. Everything else is optional.</span>}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            {isNew ? 'Add person' : 'Save'}
          </button>
        </>
      }
    >
      <form
        ref={formRef}
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="photo-row">
          <Avatar person={p} size="xl" />
          <div className="stack tight">
            <div className="btn-row">
              <button type="button" className="btn small" data-field="photo" onClick={() => fileRef.current?.click()}>
                <Camera size={14} /> {p.photo ? 'Change photo' : 'Add photo'}
              </button>
              {p.photo && (
                <button type="button" className="btn small ghost danger" onClick={() => set({ photo: undefined })}>
                  Remove
                </button>
              )}
            </div>
            <span className="faint small-text">Photos are cropped square and kept small, in this browser only.</span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (!f) return;
              try {
                set({ photo: await photoFromBlob(f) });
              } catch {
                toast("Couldn't read that image.", 'error');
              }
            }}
          />
        </div>

        <div className="section-title">
          Names <span className="spacer" />
          <button type="button" className="btn small" onClick={() => set({ names: [...p.names, { ...emptyName(), type: 'chosen' }] })}>
            <Plus size={14} /> Add name
          </button>
        </div>
        {allHidden && <div className="callout warn">Every name is hidden, so this person will show as “Unnamed person.”</div>}
        {p.names.map((n, i) => {
          const dead = n.type === 'deadname';
          return (
            <div key={i} className={`name-card${isHiddenName(n) ? ' hidden-name' : ''}`}>
              <div className="row wrap">
                <Select value={n.type} onChange={(type) => setName(i, { type })} options={NAME_TYPE_LABELS} ariaLabel="Name type" />
                <span className="spacer" style={{ flex: 1 }} />
                {i === p.names.findIndex((x) => !isHiddenName(x)) && <span className="badge accent">Shown name</span>}
                <label className="check" title={dead ? 'Deadnames are always hidden.' : 'Hidden names never appear in the app, in search, or in exports (unless you choose to include them).'}>
                  <input type="checkbox" checked={isHiddenName(n)} disabled={dead} onChange={(e) => setName(i, { hidden: e.target.checked })} />
                  <EyeOff size={14} /> {dead ? 'Always hidden' : 'Hide this name'}
                </label>
                <button type="button" className="btn icon small ghost" onClick={() => moveName(i, -1)} disabled={i === 0} aria-label="Move name up" title="Move up (the first visible name is the one shown)">
                  <ArrowUp size={14} />
                </button>
                <button type="button" className="btn icon small ghost" onClick={() => moveName(i, 1)} disabled={i === p.names.length - 1} aria-label="Move name down">
                  <ArrowDown size={14} />
                </button>
                <button type="button" className="btn icon small ghost danger" onClick={() => set({ names: p.names.filter((_, j) => j !== i) })} aria-label="Remove name" disabled={p.names.length === 1}>
                  <Trash2 size={14} />
                </button>
              </div>
              {dead && <div className="faint small-text">A deadname is never shown, searched, or exported unless you choose to include hidden names in an export.</div>}
              <div className="grid-2">
                <Field label="First and middle names">
                  <input className="input" value={n.given} onChange={(e) => setName(i, { given: e.target.value })} autoFocus={i === 0 && !field} data-field={i === 0 ? 'name' : undefined} />
                </Field>
                <Field label="Last name">
                  <input className="input" value={n.surname} onChange={(e) => setName(i, { surname: e.target.value })} />
                </Field>
              </div>
              <div className="grid-3">
                <Field label="Nickname" hint="A short form of this name, like Bob for Robert">
                  <input className="input" value={n.nickname ?? ''} onChange={(e) => setName(i, { nickname: e.target.value || undefined })} />
                </Field>
                <Field label="Prefix" hint="Dr., Rev., Hon.">
                  <input className="input" value={n.prefix ?? ''} onChange={(e) => setName(i, { prefix: e.target.value || undefined })} />
                </Field>
                <Field label="Suffix" hint="Jr., III">
                  <input className="input" value={n.suffix ?? ''} onChange={(e) => setName(i, { suffix: e.target.value || undefined })} />
                </Field>
              </div>
            </div>
          );
        })}

        <div className="section-title">Identity</div>
        <div className="grid-3">
          <Field label="Gender" htmlFor={genderId}>
            <span data-field="gender" style={{ display: 'block' }}>
            <GenderPicker
              id={genderId}
              value={p.gender.identity}
              onChange={(identity) => {
                // Fill in he/him or she/her if no pronouns are recorded (or the last ones were filled in here).
                const cur = p.pronouns?.trim() ?? '';
                if (!cur || cur === suggested.current) {
                  suggested.current = defaultPronouns(identity) ?? '';
                  set({ gender: { ...p.gender, identity }, pronouns: suggested.current || undefined });
                } else set({ gender: { ...p.gender, identity } });
              }}
            />
            </span>
          </Field>
          {p.gender.identity === 'custom' ? (
            <Field label="Self-described gender">
              <input className="input" value={p.gender.label ?? ''} placeholder="e.g., Two-Spirit, agender" onChange={(e) => set({ gender: { ...p.gender, label: e.target.value } })} />
            </Field>
          ) : (
            <div className="hide-sm" />
          )}
          <Field label="Pronouns" htmlFor={pronounsId} hint={p.pronouns && p.pronouns === suggested.current ? 'Filled in from gender. Change if needed.' : undefined}>
            <PronounPicker id={pronounsId} value={p.pronouns} onChange={(pronouns) => set({ pronouns })} />
          </Field>
        </div>
        <div className="grid-2">
          <Field
            label="Relationship words"
            hint={(p.terms ?? 'auto') === 'auto' ? `Automatic is using ${auto.terms} words${TERM_REASONS[auto.reason] ? `, ${TERM_REASONS[auto.reason]}` : ''}.` : 'Used whenever this person is described, e.g. as someone’s parent or sibling.'}
          >
            <Select value={p.terms ?? 'auto'} onChange={(terms) => set({ terms: terms === 'auto' ? undefined : terms })} options={TERM_OPTIONS} />
          </Field>
          <Field
            label="Living status"
            hint={p.living === null && presumedDeceased(p) ? 'Their records show they have died (a death record, or dates over 100 years ago).' : 'Not recorded means neither living nor deceased is known.'}
          >
            <span data-field="living" style={{ display: 'block' }}>
            <Select
              value={p.living === null ? 'auto' : p.living ? 'living' : 'deceased'}
              onChange={(v) => set({ living: v === 'auto' ? null : v === 'living' })}
              options={[
                { value: 'auto', label: presumedDeceased(p) ? 'Deceased, from their records' : 'Not recorded' },
                { value: 'living', label: 'Living' },
                { value: 'deceased', label: 'Deceased' },
              ]}
            />
            </span>
          </Field>
        </div>

        <div className="section-title">
          Life events <span className="spacer" />
          <AddEventMenu onAdd={(type) => set({ events: [...p.events, newEvent(type)] })} />
        </div>
        {!p.events.length && <div className="faint small-text">No events yet. Add a birth, residence, job, or anything else.</div>}
        {[...p.events]
          .sort((a, b) => dateSortKey(a.date) - dateSortKey(b.date))
          .map((e) => (
            <div key={e.id} className="event-card">
              <div className="head">
                <Select value={e.type} onChange={(type) => setEvent(e.id, { type })} options={EVENT_ORDER.map((t) => ({ value: t, label: EVENT_LABELS[t] }))} ariaLabel="Event type" />
                {(e.type === 'custom' || e.type === 'occupation' || e.type === 'education' || e.type === 'military') && (
                  <input
                    className="input"
                    placeholder={e.type === 'custom' ? 'What happened?' : e.type === 'occupation' ? 'Job title' : e.type === 'education' ? 'School or degree' : 'Branch or unit'}
                    value={e.label ?? ''}
                    data-field={`label:${e.id}`}
                    onChange={(ev) => setEvent(e.id, { label: ev.target.value || undefined })}
                  />
                )}
                <button type="button" className="btn icon small ghost danger" onClick={() => set({ events: p.events.filter((x) => x.id !== e.id) })} aria-label="Remove event">
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="grid-2">
                <Field label="Date">
                  <span data-field={`date:${e.id}`} style={{ display: 'block' }}>
                    <DateInput value={e.date} onChange={(date) => setEvent(e.id, { date })} />
                  </span>
                </Field>
                <Field label="Place">
                  <PlaceInput className="input" value={e.place ?? ''} placeholder="City, County, State, Country" data-field={`place:${e.id}`} onChange={(place) => setEvent(e.id, { place: place || undefined })} />
                </Field>
              </div>
              <input className="input" placeholder="Note (optional)" aria-label="Event note" value={e.note ?? ''} onChange={(ev) => setEvent(e.id, { note: ev.target.value || undefined })} />
              {e.passthrough?.some((x) => x.tag !== '__PLAC') && <div className="faint small-text">Has imported source citations or details that will be kept on export.</div>}
            </div>
          ))}

        <div className="section-title">Tags</div>
        <div className="tag-input">
          {p.tags.map((t) => (
            <span key={t} className="chip">
              {t}
              <button type="button" aria-label={`Remove tag ${t}`} onClick={() => set({ tags: p.tags.filter((x) => x !== t) })}>
                ×
              </button>
            </span>
          ))}
          <input
            value={tagText}
            placeholder={p.tags.length ? '' : 'Type a tag and press Enter'}
            aria-label="Add tag"
            onChange={(e) => setTagText(e.target.value)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ',') && tagText.trim()) {
                e.preventDefault();
                if (!p.tags.includes(tagText.trim())) set({ tags: [...p.tags, tagText.trim()] });
                setTagText('');
              } else if (e.key === 'Enter') e.preventDefault();
              else if (e.key === 'Backspace' && !tagText && p.tags.length) set({ tags: p.tags.slice(0, -1) });
            }}
          />
        </div>

        <div className="section-title">Notes</div>
        <textarea className="textarea" value={p.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Stories, memories, research notes…" rows={5} aria-label="Notes" />
        {!!p.passthrough?.length && (
          <div className="faint small-text">
            {p.passthrough.length} imported GEDCOM record{p.passthrough.length === 1 ? '' : 's'} (sources, media, or other details) will be kept when you export.
          </div>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function AddEventMenu({ onAdd }: { onAdd: (t: EventType) => void }) {
  return (
    <select
      className="select"
      style={{ width: 'auto', minHeight: 26, padding: '2px 28px 2px 8px', fontSize: 13 }}
      value=""
      aria-label="Add event"
      onChange={(e) => {
        if (e.target.value) onAdd(e.target.value as EventType);
      }}
    >
      <option value="">+ Add event…</option>
      {EVENT_ORDER.map((t) => (
        <option key={t} value={t}>
          {EVENT_LABELS[t]}
        </option>
      ))}
    </select>
  );
}
